/**
 * Chroma speaker color system — "every voice gets a color".
 *
 * Each speaker in a conversation owns a hue, used everywhere: transcript
 * bubbles, avatar circles, "who" dot stacks, and charts. Hues cycle after 6.
 *
 * The palette maps 1:1 to the CSS variables `--speaker-1` .. `--speaker-6`
 * defined in globals.css, and to the Tailwind `speaker.1` .. `speaker.6`
 * color scale in tailwind.config.ts.
 */

export interface SpeakerColor {
  /** 1-based speaker position in the palette (1..6). */
  index: number;
  /** Human label, e.g. "Violet". */
  name: string;
  /** Solid hex for the hue (avatar background, bubble border, dot). */
  hex: string;
  /** Readable hex for text of this hue on a white surface (amber is darkened). */
  textHex: string;
  /** CSS variable name (without hsl wrapper), e.g. "--speaker-1". */
  cssVar: string;
  /** The hue as an hsl() string using the CSS variable, e.g. "hsl(var(--speaker-1))". */
  hsl: string;
  /** Same, for the readable text variant. */
  textHsl: string;
  /** Tailwind class for the bubble's left border (`border-l-speaker-1` style). */
  borderClass: string;
  /** Tailwind class for avatar / dot background. */
  bgClass: string;
  /** Tailwind class for readable speaker-name text (amber uses darkened variant). */
  textClass: string;
}

/**
 * The six brand hues, in order. Speakers beyond 6 cycle back to the start.
 */
export const SPEAKER_COLORS: SpeakerColor[] = [
  {
    index: 1,
    name: "Violet",
    hex: "#7B61FF",
    textHex: "#7B61FF",
    cssVar: "--speaker-1",
    hsl: "hsl(var(--speaker-1))",
    textHsl: "hsl(var(--speaker-1))",
    borderClass: "border-speaker-1",
    bgClass: "bg-speaker-1",
    textClass: "text-speaker-1",
  },
  {
    index: 2,
    name: "Teal",
    hex: "#00A8A8",
    textHex: "#00A8A8",
    cssVar: "--speaker-2",
    hsl: "hsl(var(--speaker-2))",
    textHsl: "hsl(var(--speaker-2))",
    borderClass: "border-speaker-2",
    bgClass: "bg-speaker-2",
    textClass: "text-speaker-2",
  },
  {
    index: 3,
    name: "Amber",
    hex: "#F5A623",
    // Amber needs a darkened variant for readable text on white.
    textHex: "#B97C0E",
    cssVar: "--speaker-3",
    hsl: "hsl(var(--speaker-3))",
    textHsl: "hsl(var(--speaker-3-text))",
    borderClass: "border-speaker-3",
    bgClass: "bg-speaker-3",
    textClass: "text-speaker-3-text",
  },
  {
    index: 4,
    name: "Coral",
    hex: "#FF6B6B",
    textHex: "#FF6B6B",
    cssVar: "--speaker-4",
    hsl: "hsl(var(--speaker-4))",
    textHsl: "hsl(var(--speaker-4))",
    borderClass: "border-speaker-4",
    bgClass: "bg-speaker-4",
    textClass: "text-speaker-4",
  },
  {
    index: 5,
    name: "Blue",
    hex: "#3D8BFD",
    textHex: "#3D8BFD",
    cssVar: "--speaker-5",
    hsl: "hsl(var(--speaker-5))",
    textHsl: "hsl(var(--speaker-5))",
    borderClass: "border-speaker-5",
    bgClass: "bg-speaker-5",
    textClass: "text-speaker-5",
  },
  {
    index: 6,
    name: "Pink",
    hex: "#E85D9E",
    textHex: "#E85D9E",
    cssVar: "--speaker-6",
    hsl: "hsl(var(--speaker-6))",
    textHsl: "hsl(var(--speaker-6))",
    borderClass: "border-speaker-6",
    bgClass: "bg-speaker-6",
    textClass: "text-speaker-6",
  },
];

/**
 * Get the color for a speaker by zero-based index. Cycles after 6 speakers.
 *
 * @param index Zero-based speaker index (0 = first speaker → Violet).
 * @returns The {@link SpeakerColor} for that speaker.
 *
 * @example
 * const c = getSpeakerColor(0); // Violet
 * <div className={cn("border-l-[3px]", c.borderClass)}>...</div>
 * <span className={c.textClass}>{speakerName}</span>
 * <span className={cn("rounded-full text-white", c.bgClass)}>{initial}</span>
 */
export function getSpeakerColor(index: number): SpeakerColor {
  const count = SPEAKER_COLORS.length;
  // Normalize negatives and out-of-range values into 0..count-1.
  const normalized = ((Math.trunc(index) % count) + count) % count;
  return SPEAKER_COLORS[normalized];
}

/**
 * Convenience: get the readable text color (hex) for a speaker index.
 * Amber returns its darkened variant so it stays legible on white.
 */
export function getSpeakerTextHex(index: number): string {
  return getSpeakerColor(index).textHex;
}
